import { cn } from "@/lib/utils";
import type { CustomJobStatus } from "@/modules/custom-jobs/domain/types";
import { customJobStatusLabel } from "./job-status";

const WORKFLOW_STEPS: Array<{
  status: CustomJobStatus;
  shortLabel: string;
}> = [
    { status: "pending_confirm", shortLabel: "ຢືນຢັນ" },
    { status: "queued", shortLabel: "ລໍຖ້າເລີ່ມ" },
    { status: "in_progress", shortLabel: "ກຳລັງເຮັດ" },
    { status: "ready", shortLabel: "ພ້ອມຮັບ" },
    { status: "completed", shortLabel: "ສົ່ງມອບ" },
  ];

const STATUS_ORDER: CustomJobStatus[] = WORKFLOW_STEPS.map((s) => s.status);

function stepIndex(status: CustomJobStatus) {
  if (status === "cancelled") return -1;
  return STATUS_ORDER.indexOf(status);
}

type CustomJobWorkflowBarProps = {
  status: CustomJobStatus;
  className?: string;
};

export function CustomJobWorkflowBar({
  status,
  className,
}: CustomJobWorkflowBarProps) {
  const current = stepIndex(status);
  const isCancelled = status === "cancelled";

  return (
    <div className={cn("px-4 py-4 sm:px-5", className)}>
      {isCancelled ? (
        <p className="rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-center text-destructive text-sm">
          {customJobStatusLabel(status)}
        </p>
      ) : (
        <ol className="flex items-center gap-1 sm:gap-2">
          {WORKFLOW_STEPS.map((step, index) => {
            const done = current > index;
            const active = current === index;
            return (
              <li key={step.status} className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
                <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                  <span
                    className={cn(
                      "flex size-7 shrink-0 items-center justify-center rounded-full font-medium text-xs transition-colors sm:size-8",
                      done && "bg-leaf text-leaf-foreground",
                      active && "bg-secondary text-secondary-foreground ring-2 ring-secondary/30",
                      !done && !active && "bg-muted text-muted-foreground",
                    )}
                  >
                    {done ? "✓" : index + 1}
                  </span>
                  <span
                    className={cn(
                      "hidden w-full truncate text-center text-[10px] leading-tight sm:block sm:text-xs",
                      active ? "font-medium text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {step.shortLabel}
                  </span>
                </div>
                {index < WORKFLOW_STEPS.length - 1 ? (
                  <div
                    className={cn(
                      "mb-4 h-0.5 min-w-2 flex-1 rounded-full sm:mb-5",
                      done ? "bg-leaf/60" : "bg-border/80",
                    )}
                  />
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

export { WORKFLOW_STEPS };
